import React, { useEffect, useRef, useState } from 'react'
import { Button, Modal, Space, Typography, Input, message, Badge, Tag, App } from 'antd'
import FilePreviewOverlay from './FilePreviewOverlay'
import styled from 'styled-components'
import { needsUrlRefresh } from './presignedUrl'

const { Paragraph, Text } = Typography

const attachmentUrl = att => att.previewUrl || `${global.WatchStorage.backendUrl}/api/v1/files/${att.s3Key}`

// HOR-69: Anhang nach dem Neu-Laden ueber seinen S3-Key wiederfinden (Nachrichten-IDs koennen fehlen).
const findAttachmentUrl = (list, s3Key) => {
  if (!s3Key) return null

  for (const item of list || []) {
    const hit = (item?.attachments || []).find(att => att?.s3Key === s3Key)
    if (hit?.previewUrl) return hit.previewUrl
  }

  return null
}
const { TextArea } = Input

const Box = styled.div`
  height: 360px;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 8px;
  margin-bottom: 12px;
`
const MessageWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${p => (p.$isUser ? 'flex-end' : 'flex-start')};
  margin-bottom: 12px;
`
const Bubble = styled.div`
  display: inline-block;
  background: ${p => (p.$isUser ? '#f6ffed' : '#fafafa')};
  border: 1px solid ${p => (p.$isUser ? '#b7eb8f' : '#f0f0f0')};
  border-radius: ${p => (p.$isUser ? '12px 12px 0 12px' : '12px 12px 12px 0')};
  padding: 6px 10px 8px 10px;
  max-width: 80%;
  text-align: left;
  box-shadow: ${p => (p.$isUser ? '0 2px 3px rgba(72,187,120,0.2)' : '0 2px 3px rgba(0,0,0,0.05)')};
`
const Author = styled.div`
  color: #888;
  font-size: 12px;
  margin-bottom: 4px;
  font-weight: 600;
`
const Meta = styled(Text)`
  && {
    font-size: 11px;
    color: #aaa;
    display: block;
    text-align: ${p => (p.$isUser ? 'right' : 'left')};
  }
`
const RightAlign = styled.div`
  text-align: right;
`
const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`

const AttachmentThumb = styled.img`
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
  cursor: pointer;
  border: 2px solid #e2e8f0;
  display: inline-block;
  margin-right: 4px;
  margin-top: 4px;

  &:hover {
    border-color: #4299e1;
  }
`

const PDF = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 6px;
  border: 2px solid #e2e8f0;
  background: #f7fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #e53e3e;
`

const StyledTag = styled(Tag)`
  && {
    padding: 5px 15px;
    border-radius: 8px;
    margin-bottom: 10px;
  }
`

const STATUS_META = {
  open: { color: 'blue', label: i18n('verification.status.open') },
  inReview: { color: 'orange', label: i18n('verification.status.inReview') },
  needsInfo: { color: 'orange', label: i18n('verification.status.needsInfo') },
  approved: { color: 'green', label: i18n('verification.status.verified') },
  rejected: { color: 'red', label: i18n('verification.status.rejected') },
  closed: { color: 'default', label: i18n('verification.status.closed') },
}

export default function ChatModal({ isOpen, onClose, requestId, make, model }) {
  const { message, modal } = App.useApp()
  const [messages, setMessages] = useState([])
  const [text, setText] = useState('')
  const [requesting, setRequesting] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewImage, setPreviewImage] = useState(null)
  const [verificationData, setVerificationData] = useState(null)
  const boxRef = useRef(null)
  const mountedRef = useRef(true)
  // HOR-69: Zeitpunkt der letzten Nachrichten-Antwort (= Alter der presigned Anhang-Links) und
  // Ziel der offenen Vorschau, damit "Vorschau neu laden" den frischen Link zuordnen kann.
  const loadedAtRef = useRef(0)
  const previewTargetRef = useRef(null)

  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false
    }
  }, [])

  const send = async () => {
    const value = text.trim()
    if (!value || !requestId) return
    try {
      const res = await fetch(`${global.WatchStorage.backendUrl}/api/v1/admin/verification-requests/reply`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${global.WatchStorage.session.token}`,
        },
        body: JSON.stringify({ verificationId: requestId, message: value }),
      })
      if (res.ok || res.status === 201) {
        setText('')
        load()
      }
    } catch (e) {
      console.error(e)
    }
  }

  // Liefert die frische Nachrichtenliste (oder null bei Fehler), damit HOR-69 Links zuordnen kann.
  const load = async () => {
    if (!requestId) return null
    try {
      const res = await fetch(`${global.WatchStorage.backendUrl}/api/v1/verification-requests/${requestId}/messages`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${global.WatchStorage.session.token}` },
      })
      if (res.ok) {
        const data = await res.json()
        const list = Array.isArray(data) ? data : []
        loadedAtRef.current = Date.now()
        setMessages(list)
        return list
      }
    } catch (e) {
      console.error(e)
    }
    return null
  }

  /**
   * HOR-69: Anhang-Vorschau oeffnen. Die Nachrichten werden zwar alle 5 s gepollt (frische Links),
   * nach Netzausfall oder gedrosseltem Hintergrund-Tab kann der Link trotzdem abgelaufen sein –
   * dann erst neu laden und die Vorschau mit dem frischen Link zeigen.
   */
  const openAttachment = async (att, isPdf) => {
    let url = attachmentUrl(att)
    let refreshFailed = false

    if (needsUrlRefresh([url], loadedAtRef.current)) {
      const fresh = await load()

      if (!mountedRef.current) return

      const freshUrl = findAttachmentUrl(fresh, att.s3Key)

      if (freshUrl) url = freshUrl
      else refreshFailed = true
    }

    previewTargetRef.current = { s3Key: att.s3Key, isPdf }
    setPreviewImage({ url, isPdf, refreshFailed })
    setPreviewOpen(true)
  }

  const reloadAttachment = async () => {
    const target = previewTargetRef.current

    if (!target) return false

    const fresh = await load()

    if (!mountedRef.current) return false

    const url = findAttachmentUrl(fresh, target.s3Key)

    if (!url) {
      setPreviewImage(prev => (prev ? { ...prev, refreshFailed: true } : prev))
      return false
    }

    setPreviewImage({ url, isPdf: target.isPdf, refreshFailed: false })

    return true
  }

  useEffect(() => {
    if (!isOpen || !requestId) return

    const loadVerification = async () => {
      try {
        const res = await fetch(`${global.WatchStorage.backendUrl}/api/v1/verification-requests/${requestId}`, {
          credentials: 'include',
          headers: { Authorization: `Bearer ${global.WatchStorage.session.token}` },
        })
        if (res.ok) {
          const data = await res.json()
          setVerificationData(data)
        }
      } catch (e) {
        console.error(e)
      }
    }
    let timer = null
    // Review ADM-3: cancelled-Flag wie in ServiceChatPanel. Laeuft das Cleanup (Modal geschlossen)
    // waehrend eines await in loop(), wuerde danach ein neuer Timer gesetzt, den niemand mehr
    // loescht – die Schleife pollt dann endlos weiter gegen /verification-requests/:id(/messages).
    let cancelled = false
    const loop = async () => {
      if (cancelled) return
      await load()
      if (cancelled) return
      await loadVerification()
      if (cancelled) return
      timer = setTimeout(loop, 5000)
    }
    loop()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [requestId, isOpen])

  useEffect(() => {
    if (boxRef.current) {
      boxRef.current.scrollTo({ top: boxRef.current.scrollHeight, behavior: 'smooth' })
    }
  }, [messages.length])

  const requestInfo = async () => {
    const value = text.trim()
    if (!value || !requestId) {
      message.warning(i18n('verification.chat.requestDataHint'))
      return
    }
    setRequesting(true)
    try {
      const res = await fetch(`${global.WatchStorage.backendUrl}/api/v1/admin/verification-requests/request-info`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${global.WatchStorage.session.token}`,
        },
        body: JSON.stringify({ verificationId: requestId, message: value, requestedFields: {} }),
      })
      if (res.ok || res.status === 201) {
        setText('')
        message.success(i18n('verification.chat.requestDataSuccess'))
        load()
      } else {
        message.error(i18n('verification.chat.requestDataError'))
      }
    } catch (e) {
      console.error(e)
      message.error(i18n('verification.chat.requestDataError'))
    } finally {
      setRequesting(false)
    }
  }

  return (
    <Modal open={isOpen} onCancel={onClose} title={`${i18n('verification.chat.title', { make })}${model ? `, ${model}` : ''}`} footer={null} width={720} centered>
      <StyledTag color={STATUS_META[verificationData?.status]?.color || 'default'}>
        {i18n(`verification.status.${verificationData?.status}`) || i18n('common.unknown')}
      </StyledTag>
      <Box ref={boxRef}>
        {messages.map(m => {
          const isUser = m.authorType === 'administrator'
          return (
            <MessageWrap key={m.id || `${m.createdAt}-${Math.random()}`} $isUser={isUser}>
              <Bubble $isUser={isUser}>
                {!isUser && <Author>{m.author.name}</Author>}
                <Paragraph style={{ marginBottom: 4, whiteSpace: 'pre-wrap' }}>{m.text || ''}</Paragraph>

                {(m.attachments || []).map((att, idx) => {
                  const fileUrl = attachmentUrl(att)
                  const isPdf = att.mimeType === 'application/pdf' || att.s3Key?.toLowerCase().endsWith('.pdf')

                  return (
                    <div
                      key={idx}
                      style={{
                        display: 'inline-block',
                        marginRight: 8,
                        marginTop: 4,
                        textAlign: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={() => openAttachment(att, isPdf)}
                    >
                      {isPdf ? <PDF>📄</PDF> : <AttachmentThumb src={fileUrl} alt={`${i18n('verification.chat.attachment')} ${idx + 1}`} />}
                      <div style={{ fontSize: 11, color: '#555' }}>{isPdf ? 'PDF' : i18n('common.image')}</div>
                    </div>
                  )
                })}

                <Meta $isUser={isUser} type="secondary">
                  {new Date(m.createdAt).toLocaleString()}
                </Meta>
              </Bubble>
            </MessageWrap>
          )
        })}
      </Box>

      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <TextArea
          placeholder={i18n('verification.chat.writePlaceholder')}
          value={text}
          onChange={e => setText(e.target.value)}
          rows={2}
          onPressEnter={e => {
            if (!e.shiftKey) {
              e.preventDefault()
              send()
            }
          }}
        />
        <RightAlign>
          <ButtonRow>
            <Button
              type="default"
              onClick={requestInfo}
              disabled={verificationData?.status === 'approved' || verificationData?.status === 'rejected' || verificationData?.status === 'closed'}
              loading={requesting}
            >
              {i18n('verification.chat.requestData')}
            </Button>
            <Button
              disabled={verificationData?.status === 'approved' || verificationData?.status === 'rejected' || verificationData?.status === 'closed'}
              type="primary"
              onClick={send}
            >
              {i18n('common.send')}
            </Button>
          </ButtonRow>
        </RightAlign>
        {verificationData && verificationData.status !== 'approved' && verificationData.status !== 'rejected' && verificationData.status !== 'closed' && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
            <Space size="middle" style={{ width: '100%', justifyContent: 'center' }}>
              <Button
                type="primary"
                size="large"
                style={{ background: '#52c41a', borderColor: '#52c41a' }}
                onClick={async () => {
                  try {
                    const res = await fetch(`${global.WatchStorage.backendUrl}/api/v1/admin/verification-requests/approve`, {
                      method: 'POST',
                      credentials: 'include',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${global.WatchStorage.session.token}`,
                      },
                      body: JSON.stringify({ verificationId: requestId }),
                    })
                    if (res.ok || res.status === 201) {
                      message.success(i18n('verification.modal.approveSuccess'))
                      onClose()
                    } else {
                      message.error(i18n('verification.modal.approveError'))
                    }
                  } catch (e) {
                    console.error(e)
                    message.error(i18n('verification.modal.approveError'))
                  }
                }}
              >
                {i18n('verification.chat.approveBtn')}
              </Button>
              <Button
                danger
                size="large"
                onClick={() => {
                  modal.confirm({
                    title: i18n('verification.modal.rejectTitle'),
                    content: (
                      <div>
                        <Text>{i18n('verification.modal.rejectReasonLabel')}</Text>
                        <Input.TextArea id="reject-reason-chat" rows={4} placeholder={i18n('verification.modal.rejectPlaceholder')} style={{ marginTop: 12 }} />
                      </div>
                    ),
                    okText: i18n('common.decline'),
                    okType: 'danger',
                    cancelText: i18n('common.cancel'),
                    onOk: async () => {
                      const reason = document.getElementById('reject-reason-chat')?.value?.trim()
                      if (!reason) {
                        message.warning(i18n('verification.modal.rejectReasonRequired'))
                        return
                      }
                      try {
                        const res = await fetch(`${global.WatchStorage.backendUrl}/api/v1/admin/verification-requests/reject`, {
                          method: 'POST',
                          credentials: 'include',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${global.WatchStorage.session.token}`,
                          },
                          body: JSON.stringify({ verificationId: requestId, reason }),
                        })
                        if (res.ok || res.status === 201) {
                          message.success(i18n('verification.modal.rejectSuccess'))
                          onClose()
                        } else {
                          message.error(i18n('verification.modal.rejectError'))
                          return Promise.reject(new Error('API-Fehler'))
                        }
                      } catch (e) {
                        console.error(e)
                        message.error(i18n('verification.modal.rejectError'))
                        return Promise.reject(e)
                      }
                    },
                  })
                }}
              >
                {i18n('verification.chat.rejectBtn')}
              </Button>
            </Space>
          </div>
        )}
      </Space>

      {previewOpen && previewImage && (
        <FilePreviewOverlay
          url={previewImage.url}
          isPdf={previewImage.isPdf}
          refreshFailed={previewImage.refreshFailed}
          onReload={reloadAttachment}
          onClose={() => {
            setPreviewOpen(false)
            previewTargetRef.current = null
          }}
        />
      )}
    </Modal>
  )
}
